// Statutory filings register — the filing register for one period (gateway
// Phase 4). One register + a detail drawer: outstanding obligations and
// generated filings per legal entity, with the generate → approve → submit →
// outcome → payment flow.
//
// The register now lives on the Finance hub (payroll/finance?tab=filings); the
// old payroll/filings route redirects there so links keep working.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  FileCheck,
  Paperclip,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { Input } from '@/features/shared/components/ui/input';
import { Textarea } from '@/features/shared/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/features/shared/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/features/shared/components/ui/sheet';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { cn } from '@/lib/utils';
import { payrollApi } from '../api/payrollApi';
import type {
  Filing,
  FilingAmount,
  FilingCounts,
  FilingIssue,
  FilingOutcomeInput,
  OutstandingFiling,
  FilingRegister,
} from '../types';
import { FILING_STATUS_META, FILING_TYPE_LABEL, FILING_TYPE_ORDER, filingStatusMeta } from '../lib/state';
import { formatDate } from '../lib/format';
import { formatMoney } from '@/lib/money';
import { StatusChip } from '../components/StatusChip';

const MAX_EVIDENCE_BYTES = 15 * 1024 * 1024; // 15MB (backend limit)

type TFn = (key: string, opts?: any) => string;

interface Period {
  year: number;
  month: number;
}

/** "2026-06" → {year, month}, or null when unparsable. */
function parsePeriod(period: string): Period | null {
  const m = /^(\d{4})-(\d{2})$/.exec(period || '');
  return m ? { year: Number(m[1]), month: Number(m[2]) } : null;
}

function periodLabel(p: Period): string {
  return new Date(p.year, p.month - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function stepPeriod(p: Period, delta: number): Period {
  const d = new Date(p.year, p.month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

/** A filing whose amount is calculated → "TZS 1,250,000"; otherwise null.
 *  Uncalculated is never rendered as 0 — callers show "Not calculated yet". */
export function filingAmountLabel(a: FilingAmount | null | undefined): string | null {
  if (!a) return null;
  const n = Number(a.amount);
  if (!Number.isFinite(n)) return null;
  return formatMoney(n, a.currency);
}

/** The register's header counts — the server's (computed over the companies the
 *  caller can see); recomputed from the returned rows only for an older backend
 *  that sent none (those rows are already reach-bounded too). */
export function filingCounts(register: FilingRegister | null): FilingCounts {
  if (register?.counts) return register.counts;
  const outstanding = register?.outstanding ?? [];
  const filings = register?.filings ?? [];
  return {
    outstanding: outstanding.length,
    overdue: outstanding.filter((o) => o.overdue).length + filings.filter((f) => f.overdue).length,
    completed: filings.filter((f) => f.status === 'completed').length,
  };
}

const isNotFound = (e: any): boolean => e?.response?.status === 404;

// One table row — either a not-yet-generated obligation or an actual filing.
type Row =
  | { kind: 'outstanding'; key: string; entityId: string; entityName: string; o: OutstandingFiling }
  | { kind: 'filing'; key: string; entityId: string; entityName: string; f: Filing };

/** The filing-register body (period stepper, register table, dialogs, drawer)
 *  — rendered by the Finance hub's Filings tab. `onOverdueChange` lets the hub
 *  keep its overdue badge in sync with the loaded period. */
export const FilingsRegister: React.FC<{
  onOverdueChange?: (count: number) => void;
  /** Land on a specific period (e.g. deep-linked from a locked run) instead of
   *  the server default (previous month). */
  initialPeriod?: Period | null;
}> = ({ onOverdueChange, initialPeriod }) => {
  const { t } = useTranslation('payroll');
  const confirm = useConfirm();
  const { canViewPayroll, canRunPayroll, canApprovePayroll } = useRoleAccess();

  const [register, setRegister] = useState<FilingRegister | null>(null);
  const [period, setPeriod] = useState<Period | null>(null); // null until the server default arrives
  const [loading, setLoading] = useState(true);
  // A failed load is an ERROR state — never the empty "nothing on the register".
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Dialogs (422 validation findings + the per-state action prompts).
  const [validationIssues, setValidationIssues] = useState<FilingIssue[] | null>(null);
  const [submitFor, setSubmitFor] = useState<Filing | null>(null);
  const [outcomeFor, setOutcomeFor] = useState<Filing | null>(null);
  const [paymentFor, setPaymentFor] = useState<Filing | null>(null);
  // Detail drawer.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Filing | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailNotFound, setDetailNotFound] = useState(false);

  const load = useCallback(async (p?: Period | null, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const reg = await payrollApi.getFilingRegister(p?.year, p?.month);
      setRegister(reg);
      setLoadError(null);
      const parsed = parsePeriod(reg.period);
      if (parsed) setPeriod(parsed);
    } catch (e: any) {
      if (!silent) {
        setRegister(null);
        const msg = getApiErrorMessage(e, t('filings.loadError', 'Could not load the filing register.'));
        setLoadError(msg);
        toast.error(msg);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // initialPeriod (a deep-link from a run) wins over the server default.
    void load(initialPeriod ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, initialPeriod?.year, initialPeriod?.month]);

  const openDetail = useCallback((id: string) => {
    setDetailId(id);
    setDetail(null);
    setDetailNotFound(false);
    setDetailLoading(true);
    payrollApi
      .getFiling(id)
      .then(setDetail)
      .catch((e) => {
        setDetail(null);
        // Unknown, or another company's filing outside your reach — the server
        // answers the same 404 for both, and so does the page.
        if (isNotFound(e)) {
          setDetailNotFound(true);
          return;
        }
        toast.error(getApiErrorMessage(e, t('filings.detailError', 'Could not load the filing.')));
      })
      .finally(() => setDetailLoading(false));
  }, [t]);

  /** Silent refetch of the register (and the open drawer) after any action. */
  const refresh = useCallback(async () => {
    await load(period, true);
    if (detailId) {
      try {
        setDetail(await payrollApi.getFiling(detailId));
      } catch {
        /* keep the last good detail */
      }
    }
  }, [load, period, detailId]);

  const step = (delta: number) => {
    if (!period || loading) return;
    void load(stepPeriod(period, delta));
  };

  // --- actions ---------------------------------------------------------------

  async function generate(o: OutstandingFiling) {
    const p = parsePeriod(o.period) ?? period;
    if (!p) return;
    const key = `gen:${o.legalEntityId}:${o.filingType}`;
    setBusyKey(key);
    try {
      const f = await payrollApi.generateFiling({
        legalEntityId: o.legalEntityId,
        filingType: o.filingType,
        year: p.year,
        month: p.month,
      });
      toast.success(t('filings.generated', {
        defaultValue: '{{type}} generated.',
        type: FILING_TYPE_LABEL[o.filingType],
      }));
      await refresh();
      openDetail(f.id);
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 422 && data?.code === 'validation_failed') {
        setValidationIssues(
          Array.isArray(data.validation)
            ? data.validation.map((v: any) => ({
                level: v?.level === 'error' ? 'error' : 'warning',
                code: v?.code ?? '',
                message: v?.message ?? '',
              }))
            : [],
        );
      } else if (e?.response?.status === 409) {
        toast.error(t('filings.alreadyExists', 'This filing already exists for the period.'));
        await refresh();
      } else {
        toast.error(getApiErrorMessage(e, t('filings.generateError', 'Could not generate the filing.')));
      }
    } finally {
      setBusyKey(null);
    }
  }

  /** Run one filing action, then silently refresh the register + drawer. */
  async function act(filingId: string, fn: () => Promise<unknown>, success: string) {
    setBusyKey(`act:${filingId}`);
    try {
      await fn();
      toast.success(success);
      await refresh();
      return true;
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('errors.generic', 'Something went wrong. Please try again.')), {
        duration: 6000,
      });
      return false;
    } finally {
      setBusyKey(null);
    }
  }

  async function approve(f: Filing) {
    // 409 includes the second-reviewer rule (the creator can't approve it) —
    // the server message is surfaced verbatim by act().
    await act(f.id, () => payrollApi.approveFiling(f.id), t('filings.approvedToast', 'Filing approved.'));
  }

  async function amend(f: Filing) {
    const ok = await confirm({
      title: t('filings.amendTitle', 'Amend this filing?'),
      message: t('filings.amendMessage', {
        defaultValue:
          'This creates version {{next}} of the {{type}} for {{period}} from the current locked run data. The rejected version stays on the register for the audit trail.',
        next: f.version + 1,
        type: FILING_TYPE_LABEL[f.filingType],
        period: f.period,
      }),
      confirmLabel: t('filings.amendConfirm', 'Create amendment'),
    });
    if (!ok) return;
    setBusyKey(`act:${f.id}`);
    try {
      const next = await payrollApi.amendFiling(f.id);
      toast.success(t('filings.amendedToast', {
        defaultValue: 'Amendment created (version {{version}}).',
        version: next.version,
      }));
      await refresh();
      openDetail(next.id);
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 422 && data?.code === 'validation_failed') {
        setValidationIssues(
          Array.isArray(data.validation)
            ? data.validation.map((v: any) => ({
                level: v?.level === 'error' ? 'error' : 'warning',
                code: v?.code ?? '',
                message: v?.message ?? '',
              }))
            : [],
        );
      } else {
        toast.error(getApiErrorMessage(e, t('filings.amendError', 'Could not create the amendment.')));
      }
    } finally {
      setBusyKey(null);
    }
  }

  async function downloadFile(f: Filing) {
    try {
      const url = await payrollApi.exportDownloadUrl(f.exportId);
      if (url) window.open(url, '_blank', 'noopener');
      else toast.error(t('errors.exportDownload', 'The file is not ready to download yet.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('errors.exportDownload', 'The file is not ready to download yet.')), { duration: 6000 });
    }
  }

  async function downloadEvidence(filingId: string, evidenceId: string) {
    try {
      const url = await payrollApi.filingEvidenceUrl(filingId, evidenceId);
      if (url) window.open(url, '_blank', 'noopener');
      else toast.error(t('filings.evidenceDownloadError', 'Could not download the attachment.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('filings.evidenceDownloadError', 'Could not download the attachment.')), { duration: 6000 });
    }
  }

  // --- derived view ------------------------------------------------------------

  const summary = useMemo(() => filingCounts(register), [register]);

  // Keep the hub's overdue badge in sync with the loaded period.
  useEffect(() => {
    onOverdueChange?.(summary.overdue);
  }, [summary.overdue, onOverdueChange]);

  /** Rows grouped by legal entity, filing types in statutory order, newest version first. */
  const groups = useMemo(() => {
    const rows: Row[] = [
      ...(register?.outstanding ?? []).map<Row>((o) => ({
        kind: 'outstanding',
        key: `o:${o.legalEntityId}:${o.filingType}`,
        entityId: o.legalEntityId,
        entityName: o.legalEntityName,
        o,
      })),
      ...(register?.filings ?? []).map<Row>((f) => ({
        kind: 'filing',
        key: `f:${f.id}`,
        entityId: f.legalEntityId,
        entityName: f.legalEntityName,
        f,
      })),
    ];
    const byEntity = new Map<string, { name: string; rows: Row[] }>();
    for (const r of rows) {
      const g = byEntity.get(r.entityId) ?? { name: r.entityName, rows: [] };
      if (g.name === '—' && r.entityName !== '—') g.name = r.entityName;
      g.rows.push(r);
      byEntity.set(r.entityId, g);
    }
    const typeIdx = (r: Row) =>
      FILING_TYPE_ORDER.indexOf(r.kind === 'outstanding' ? r.o.filingType : r.f.filingType);
    for (const g of byEntity.values()) {
      g.rows.sort((a, b) => {
        const d = typeIdx(a) - typeIdx(b);
        if (d !== 0) return d;
        const va = a.kind === 'filing' ? a.f.version : 0;
        const vb = b.kind === 'filing' ? b.f.version : 0;
        return vb - va; // newest version first
      });
    }
    return Array.from(byEntity.entries())
      .map(([id, g]) => ({ id, ...g }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [register]);

  if (!canViewPayroll) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        {t('filings.noAccess', 'You need payroll access to view statutory filings.')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header: description + month stepper (the page title lives on the hub) */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-xl text-sm text-muted-foreground">
          {t('filings.description', 'PAYE, SDL, NSSF, WCF and HESLB returns — generate, approve, file and keep the proof in one register.')}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            <button
              type="button"
              aria-label={t('filings.prevMonth', 'Previous month')}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-charcoal disabled:opacity-50"
              disabled={!period || loading}
              onClick={() => step(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-32 px-1 text-center text-sm font-semibold text-charcoal">
              {period ? periodLabel(period) : '…'}
            </span>
            <button
              type="button"
              aria-label={t('filings.nextMonth', 'Next month')}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-charcoal disabled:opacity-50"
              disabled={!period || loading}
              onClick={() => step(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {!loadError && register && (
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">
          {t('filings.pillOutstanding', { defaultValue: '{{count}} outstanding', count: summary.outstanding })}
        </Badge>
        <Badge variant={summary.overdue > 0 ? 'danger' : 'secondary'}>
          {t('filings.pillOverdue', { defaultValue: '{{count}} overdue', count: summary.overdue })}
        </Badge>
        <Badge variant="success">
          {t('filings.pillCompleted', { defaultValue: '{{count}} completed', count: summary.completed })}
        </Badge>
        {(register.totalsDue ?? []).map((tot) => (
          <Badge key={tot.currency} variant="outline" data-testid="filings-total-due">
            {t('filings.pillTotalDue', {
              defaultValue: '{{amount}} due',
              amount: filingAmountLabel(tot) ?? '—',
            })}
          </Badge>
        ))}
      </div>
      )}

      {/* Register table, grouped by legal entity */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('filings.colFiling', 'Filing')}</TableHead>
                <TableHead>{t('filings.colDue', 'Due date')}</TableHead>
                <TableHead className="text-right">{t('filings.colAmount', 'Amount due')}</TableHead>
                <TableHead>{t('filings.colStatus', 'Status')}</TableHead>
                <TableHead>{t('filings.colReference', 'Reference')}</TableHead>
                <TableHead className="w-56 text-right">{t('filings.colActions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && loadError && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center">
                    <div role="alert" className="flex flex-col items-center gap-3">
                      <span className="inline-flex items-center gap-2 font-medium text-charcoal">
                        <AlertTriangle className="h-4 w-4 text-warning-strong" />
                        {t('filings.loadErrorTitle', 'The filing register could not be loaded.')}
                      </span>
                      <span className="text-sm text-muted-foreground">{loadError}</span>
                      <Button size="sm" variant="outline" onClick={() => void load(period)}>
                        {t('filings.retry', 'Try again')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {!loading && !loadError && groups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                    {t('filings.empty', 'Nothing on the register for this period yet. Obligations appear once a payroll run for the period is locked.')}
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                !loadError &&
                groups.map((g) => (
                  <React.Fragment key={g.id}>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableCell colSpan={6} className="py-2">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          <Building2 className="h-3.5 w-3.5" />
                          {g.name}
                        </span>
                      </TableCell>
                    </TableRow>
                    {g.rows.map((row) =>
                      row.kind === 'outstanding' ? (
                        <OutstandingRow
                          key={row.key}
                          o={row.o}
                          t={t}
                          canGenerate={canRunPayroll}
                          busy={busyKey === `gen:${row.o.legalEntityId}:${row.o.filingType}`}
                          onGenerate={() => void generate(row.o)}
                        />
                      ) : (
                        <FilingRow
                          key={row.key}
                          f={row.f}
                          t={t}
                          canRun={canRunPayroll}
                          canApprove={canApprovePayroll}
                          busy={busyKey === `act:${row.f.id}`}
                          onOpen={() => openDetail(row.f.id)}
                          onDownload={() => void downloadFile(row.f)}
                          onSubmit={() => setSubmitFor(row.f)}
                          onOutcome={() => setOutcomeFor(row.f)}
                          onPayment={() => setPaymentFor(row.f)}
                          onAmend={() => void amend(row.f)}
                        />
                      ),
                    )}
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* 422 validation findings (generate/amend refused) */}
      <ValidationDialog issues={validationIssues} onClose={() => setValidationIssues(null)} t={t} />

      {/* Mark submitted (optional acknowledgment ref) */}
      <SubmitDialog
        filing={submitFor}
        busy={!!submitFor && busyKey === `act:${submitFor.id}`}
        onClose={() => setSubmitFor(null)}
        onConfirm={async (ackRef) => {
          if (!submitFor) return;
          const ok = await act(
            submitFor.id,
            () => payrollApi.submitFiling(submitFor.id, ackRef || undefined),
            t('filings.submittedToast', 'Filing marked as submitted.'),
          );
          if (ok) setSubmitFor(null);
        }}
        t={t}
      />

      {/* Record the authority's outcome */}
      <OutcomeDialog
        filing={outcomeFor}
        busy={!!outcomeFor && busyKey === `act:${outcomeFor.id}`}
        onClose={() => setOutcomeFor(null)}
        onConfirm={async (body) => {
          if (!outcomeFor) return;
          const ok = await act(
            outcomeFor.id,
            () => payrollApi.recordFilingOutcome(outcomeFor.id, body),
            body.result === 'accepted'
              ? t('filings.acceptedToast', 'Outcome recorded — accepted.')
              : t('filings.rejectedToast', 'Outcome recorded — rejected.'),
          );
          if (ok) setOutcomeFor(null);
        }}
        t={t}
      />

      {/* Record the statutory payment (completes the filing) */}
      <PaymentDialog
        filing={paymentFor}
        busy={!!paymentFor && busyKey === `act:${paymentFor.id}`}
        onClose={() => setPaymentFor(null)}
        onConfirm={async (ref) => {
          if (!paymentFor) return;
          const ok = await act(
            paymentFor.id,
            () => payrollApi.recordFilingPayment(paymentFor.id, ref),
            t('filings.paymentToast', 'Payment recorded — filing completed.'),
          );
          if (ok) setPaymentFor(null);
        }}
        t={t}
      />

      {/* Detail drawer */}
      <FilingDrawer
        open={!!detailId}
        filing={detail}
        loading={detailLoading}
        notFound={detailNotFound}
        busy={!!detail && busyKey === `act:${detail.id}`}
        canRun={canRunPayroll}
        canApprove={canApprovePayroll}
        onClose={() => {
          setDetailId(null);
          setDetail(null);
          setDetailNotFound(false);
        }}
        onApprove={(f) => void approve(f)}
        onDownload={(f) => void downloadFile(f)}
        onDownloadEvidence={(f, evId) => void downloadEvidence(f.id, evId)}
        onUploaded={() => void refresh()}
        t={t}
      />
    </div>
  );
};

/** Legacy route (payroll/filings) — redirect to the Finance hub tab so there
 *  is exactly one canonical filings surface. */
export const FilingsPage: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <Navigate
      to={{ pathname: pathname.replace(/filings\/?$/, 'finance'), search: '?tab=filings' }}
      replace
    />
  );
};

// --- table rows ---------------------------------------------------------------

/** Amount due — the company's currency, or an explicit "Not calculated yet"
 *  (no approved run covers the period). Never a 0 standing in for "unknown". */
export const FilingAmountCell: React.FC<{ amount: FilingAmount | null; t: TFn }> = ({ amount, t }) => {
  const label = filingAmountLabel(amount);
  return label ? (
    <span className="font-medium tabular-nums text-charcoal">{label}</span>
  ) : (
    <span className="text-sm italic text-muted-foreground" data-testid="filing-amount-not-calculated">
      {t('filings.notCalculated', 'Not calculated yet')}
    </span>
  );
};

const OverdueBadge: React.FC<{ t: TFn }> = ({ t }) => (
  <Badge variant="danger">{t('filings.overdue', 'Overdue')}</Badge>
);

const OutstandingRow: React.FC<{
  o: OutstandingFiling;
  t: TFn;
  canGenerate: boolean;
  busy: boolean;
  onGenerate: () => void;
}> = ({ o, t, canGenerate, busy, onGenerate }) => (
  <TableRow>
    <TableCell className="font-medium text-charcoal">{FILING_TYPE_LABEL[o.filingType]}</TableCell>
    <TableCell>
      <span className="inline-flex items-center gap-2 text-muted-foreground">
        {o.dueDate ? formatDate(o.dueDate) : '—'}
        {o.overdue && <OverdueBadge t={t} />}
      </span>
    </TableCell>
    <TableCell className="text-right">
      <FilingAmountCell amount={o.amountDue} t={t} />
    </TableCell>
    <TableCell>
      <Badge variant="outline" className="text-muted-foreground">
        {t('filings.notStarted', 'Not started')}
      </Badge>
    </TableCell>
    <TableCell className="text-muted-foreground">—</TableCell>
    <TableCell className="text-right">
      {canGenerate && (
        <Button size="sm" disabled={busy} onClick={onGenerate}>
          {busy ? t('filings.generating', 'Generating…') : t('filings.generate', 'Generate')}
        </Button>
      )}
    </TableCell>
  </TableRow>
);

const FilingRow: React.FC<{
  f: Filing;
  t: TFn;
  canRun: boolean;
  canApprove: boolean;
  busy: boolean;
  onOpen: () => void;
  onDownload: () => void;
  onSubmit: () => void;
  onOutcome: () => void;
  onPayment: () => void;
  onAmend: () => void;
}> = ({ f, t, canRun, canApprove, busy, onOpen, onDownload, onSubmit, onOutcome, onPayment, onAmend }) => {
  // Row click opens the drawer; action buttons stop propagation.
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };
  const refs: string[] = [];
  if (f.controlNumber) refs.push(t('filings.controlNoShort', { defaultValue: 'Control no. {{ref}}', ref: f.controlNumber }));
  if (f.paymentReference) refs.push(t('filings.paymentRefShort', { defaultValue: 'Payment {{ref}}', ref: f.paymentReference }));
  if (refs.length === 0 && f.acknowledgmentRef) refs.push(t('filings.ackRefShort', { defaultValue: 'Ack. {{ref}}', ref: f.acknowledgmentRef }));

  return (
    <TableRow className="cursor-pointer" onClick={onOpen}>
      <TableCell className="font-medium text-charcoal">
        <span className="inline-flex items-center gap-2">
          {FILING_TYPE_LABEL[f.filingType]}
          {f.version > 1 && (
            <Badge variant="outline">{t('filings.versionShort', { defaultValue: 'v{{version}}', version: f.version })}</Badge>
          )}
        </span>
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          {f.dueDate ? formatDate(f.dueDate) : '—'}
          {f.overdue && <OverdueBadge t={t} />}
        </span>
      </TableCell>
      <TableCell className="text-right">
        <FilingAmountCell amount={f.amountDue} t={t} />
      </TableCell>
      <TableCell>
        <StatusChip meta={filingStatusMeta(f.status)} />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{refs.length > 0 ? refs.join(' · ') : '—'}</TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1.5">
          {f.status === 'generated' && (
            <>
              <Button size="sm" variant="ghost" className="gap-1.5" onClick={stop(onDownload)}>
                <Download className="h-3.5 w-3.5" />
                {t('filings.downloadFile', 'Download file')}
              </Button>
              {canApprove && (
                <Button size="sm" variant="outline" disabled={busy} onClick={stop(onOpen)}>
                  {t('filings.reviewApprove', 'Review & approve')}
                </Button>
              )}
            </>
          )}
          {f.status === 'approved' && canApprove && (
            <Button size="sm" variant="outline" disabled={busy} onClick={stop(onSubmit)}>
              {t('filings.markSubmitted', 'Mark submitted')}
            </Button>
          )}
          {f.status === 'submitted' && canApprove && (
            <Button size="sm" variant="outline" disabled={busy} onClick={stop(onOutcome)}>
              {t('filings.recordOutcome', 'Record outcome')}
            </Button>
          )}
          {f.status === 'accepted' && canApprove && (
            <Button size="sm" variant="outline" disabled={busy} onClick={stop(onPayment)}>
              {t('filings.recordPayment', 'Record payment')}
            </Button>
          )}
          {f.status === 'rejected' && canRun && (
            <Button size="sm" variant="outline" disabled={busy} onClick={stop(onAmend)}>
              {busy ? t('filings.amending', 'Amending…') : t('filings.amend', 'Amend')}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

// --- dialogs -------------------------------------------------------------------

const IssueList: React.FC<{ issues: FilingIssue[] }> = ({ issues }) => (
  <ul className="max-h-72 space-y-2 overflow-y-auto">
    {issues.map((v, i) => (
      <li key={`${v.code}-${i}`} className="flex items-start gap-2 text-sm">
        {v.level === 'error' ? (
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#991B1B]" />
        ) : (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#92400E]" />
        )}
        <span className="text-foreground">
          {v.message}
          {v.code && <span className="ml-1.5 text-xs text-muted-foreground">({v.code})</span>}
        </span>
      </li>
    ))}
  </ul>
);

const ValidationDialog: React.FC<{
  issues: FilingIssue[] | null;
  onClose: () => void;
  t: TFn;
}> = ({ issues, onClose, t }) => (
  <Dialog open={!!issues} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>{t('filings.validationTitle', 'Filing could not be generated')}</DialogTitle>
        <DialogDescription>
          {t('filings.validationDescription', 'Fix the errors below in the payroll run, then generate again. Warnings are informational.')}
        </DialogDescription>
      </DialogHeader>
      {issues && issues.length > 0 ? (
        <IssueList issues={issues} />
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('filings.validationNone', 'The server rejected the request but returned no findings.')}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t('filings.close', 'Close')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const SubmitDialog: React.FC<{
  filing: Filing | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (ackRef: string) => void;
  t: TFn;
}> = ({ filing, busy, onClose, onConfirm, t }) => {
  const [ackRef, setAckRef] = useState('');
  useEffect(() => {
    if (filing) setAckRef('');
  }, [filing]);
  return (
    <Dialog open={!!filing} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('filings.submitTitle', 'Mark as submitted')}</DialogTitle>
          <DialogDescription>
            {t('filings.submitReminder', 'This records that the return was actually filed on the authority’s portal or at their office (TRA, NSSF, WCF, HESLB). Nothing is transmitted from here.')}
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('filings.ackRefLabel', 'Acknowledgment reference (optional)')}
          </label>
          <Input
            value={ackRef}
            onChange={(e) => setAckRef(e.target.value)}
            placeholder={t('filings.ackRefPlaceholder', 'e.g. the portal acknowledgment number')}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('filings.cancel', 'Cancel')}
          </Button>
          <Button disabled={busy} onClick={() => onConfirm(ackRef.trim())}>
            {busy ? t('filings.saving', 'Saving…') : t('filings.markSubmitted', 'Mark submitted')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OutcomeDialog: React.FC<{
  filing: Filing | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (body: FilingOutcomeInput) => void;
  t: TFn;
}> = ({ filing, busy, onClose, onConfirm, t }) => {
  const [result, setResult] = useState<'accepted' | 'rejected'>('accepted');
  const [ackRef, setAckRef] = useState('');
  const [controlNumber, setControlNumber] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (filing) {
      setResult('accepted');
      setAckRef(filing.acknowledgmentRef ?? '');
      setControlNumber('');
      setReason('');
    }
  }, [filing]);
  const reasonMissing = result === 'rejected' && reason.trim().length === 0;

  return (
    <Dialog open={!!filing} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('filings.outcomeTitle', 'Record the authority’s outcome')}</DialogTitle>
          <DialogDescription>
            {t('filings.outcomeDescription', 'What did the authority decide about this submitted return?')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-4">
            {(['accepted', 'rejected'] as const).map((r) => (
              <label key={r} className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="filing-outcome"
                  checked={result === r}
                  onChange={() => setResult(r)}
                  className="accent-orange-500"
                />
                {r === 'accepted' ? t('filings.accepted', 'Accepted') : t('filings.rejected', 'Rejected')}
              </label>
            ))}
          </div>

          {result === 'accepted' ? (
            <>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('filings.ackRefLabel2', 'Acknowledgment reference')}
                </label>
                <Input value={ackRef} onChange={(e) => setAckRef(e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('filings.controlNumberLabel', 'Control number')}
                </label>
                <Input
                  value={controlNumber}
                  onChange={(e) => setControlNumber(e.target.value)}
                  placeholder={t('filings.controlNumberPlaceholder', 'Used to pay the statutory amount')}
                />
              </div>
            </>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('filings.rejectionReasonLabel', 'Rejection reason (required)')}
              </label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('filings.rejectionReasonPlaceholder', 'What did the authority object to?')}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('filings.cancel', 'Cancel')}
          </Button>
          <Button
            variant={result === 'rejected' ? 'destructive' : 'default'}
            disabled={busy || reasonMissing}
            onClick={() =>
              onConfirm(
                result === 'accepted'
                  ? { result, acknowledgmentRef: ackRef.trim() || undefined, controlNumber: controlNumber.trim() || undefined }
                  : { result, rejectionReason: reason.trim() },
              )
            }
          >
            {busy ? t('filings.saving', 'Saving…') : t('filings.recordOutcome', 'Record outcome')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PaymentDialog: React.FC<{
  filing: Filing | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (ref: string) => void;
  t: TFn;
}> = ({ filing, busy, onClose, onConfirm, t }) => {
  const [ref, setRef] = useState('');
  useEffect(() => {
    if (filing) setRef('');
  }, [filing]);
  return (
    <Dialog open={!!filing} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('filings.paymentTitle', 'Record the statutory payment')}</DialogTitle>
          <DialogDescription>
            {t('filings.paymentDescription', 'Recording the payment completes this filing on the register.')}
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('filings.paymentRefLabel', 'Payment reference')}
          </label>
          <Input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder={t('filings.paymentRefPlaceholder', 'The receipt / control-number payment proof')}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('filings.cancel', 'Cancel')}
          </Button>
          <Button disabled={busy || ref.trim().length === 0} onClick={() => onConfirm(ref.trim())}>
            {busy ? t('filings.saving', 'Saving…') : t('filings.recordPayment', 'Record payment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- detail drawer ---------------------------------------------------------------

interface TimelineStep {
  key: string;
  label: string;
  done: boolean;
  actor?: string;
  date?: string;
  detail?: string;
}

function buildTimeline(f: Filing, t: TFn): TimelineStep[] {
  const outcomeDone = ['accepted', 'rejected', 'completed'].includes(f.status) || !!f.rejectionReason;
  const outcomeDetail = f.rejectionReason
    ? t('filings.timelineRejectedDetail', { defaultValue: 'Rejected — {{reason}}', reason: f.rejectionReason })
    : outcomeDone
      ? [
          t('filings.accepted', 'Accepted'),
          f.acknowledgmentRef ? t('filings.ackRefShort', { defaultValue: 'Ack. {{ref}}', ref: f.acknowledgmentRef }) : '',
          f.controlNumber ? t('filings.controlNoShort', { defaultValue: 'Control no. {{ref}}', ref: f.controlNumber }) : '',
        ]
          .filter(Boolean)
          .join(' · ')
      : undefined;
  return [
    {
      key: 'generated',
      label: t('filings.timelineGenerated', 'Generated'),
      done: true,
      date: f.createdAt || undefined,
    },
    {
      key: 'approved',
      label: t('filings.timelineApproved', 'Approved'),
      done: !!f.approvedAt,
      actor: f.approvedBy,
      date: f.approvedAt,
    },
    {
      key: 'submitted',
      label: t('filings.timelineSubmitted', 'Submitted to the authority'),
      done: !!f.submittedAt,
      actor: f.submittedBy,
      date: f.submittedAt,
      detail: f.acknowledgmentRef && !outcomeDone
        ? t('filings.ackRefShort', { defaultValue: 'Ack. {{ref}}', ref: f.acknowledgmentRef })
        : undefined,
    },
    {
      key: 'outcome',
      label: t('filings.timelineOutcome', 'Outcome'),
      done: outcomeDone,
      detail: outcomeDetail,
    },
    {
      key: 'payment',
      label: t('filings.timelinePayment', 'Payment recorded'),
      done: !!f.paymentReference,
      detail: f.paymentReference
        ? t('filings.paymentRefShort', { defaultValue: 'Payment {{ref}}', ref: f.paymentReference })
        : undefined,
    },
  ];
}

const FilingDrawer: React.FC<{
  open: boolean;
  filing: Filing | null;
  loading: boolean;
  /** The server answered 404: unknown, or outside the caller's company reach. */
  notFound?: boolean;
  busy: boolean;
  canRun: boolean;
  canApprove: boolean;
  onClose: () => void;
  onApprove: (f: Filing) => void;
  onDownload: (f: Filing) => void;
  onDownloadEvidence: (f: Filing, evidenceId: string) => void;
  onUploaded: () => void;
  t: TFn;
}> = ({ open, filing, loading, notFound, busy, canRun, canApprove, onClose, onApprove, onDownload, onDownloadEvidence, onUploaded, t }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (!open) {
      setNote('');
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [open]);

  async function upload() {
    const file = fileRef.current?.files?.[0];
    if (!filing || !file) {
      toast.error(t('filings.evidenceNoFile', 'Choose a file to attach first.'));
      return;
    }
    if (file.size > MAX_EVIDENCE_BYTES) {
      toast.error(t('filings.evidenceTooLarge', 'The file is larger than the 15MB limit.'));
      return;
    }
    setUploading(true);
    try {
      await payrollApi.uploadFilingEvidence(filing.id, file, note.trim() || undefined);
      toast.success(t('filings.evidenceUploaded', 'Attachment saved.'));
      setNote('');
      if (fileRef.current) fileRef.current.value = '';
      onUploaded();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('filings.evidenceUploadError', 'Could not upload the attachment.')));
    } finally {
      setUploading(false);
    }
  }

  const timeline = filing ? buildTimeline(filing, t) : [];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        {loading && (
          <div className="space-y-4 pt-8">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {!loading && filing && (
          <>
            <SheetHeader>
              <SheetTitle className="flex flex-wrap items-center gap-2">
                {FILING_TYPE_LABEL[filing.filingType]}
                <StatusChip meta={filingStatusMeta(filing.status)} />
                {filing.overdue && <OverdueBadge t={t} />}
              </SheetTitle>
              <SheetDescription>
                {filing.legalEntityName} · {filing.period}
                {filing.dueDate
                  ? ` · ${t('filings.dueShort', { defaultValue: 'due {{date}}', date: formatDate(filing.dueDate) })}`
                  : ''}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-5 space-y-6">
              {/* Amount due */}
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('filings.amountDue', 'Amount due')}
                </p>
                <div className="mt-1 text-lg">
                  <FilingAmountCell amount={filing.amountDue} t={t} />
                </div>
              </div>

              {/* Version / supersession */}
              {(filing.version > 1 || filing.supersededBy) && (
                <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                  {filing.version > 1 && (
                    <p>
                      {t('filings.versionNote', {
                        defaultValue: 'Version {{version}} — this amendment supersedes an earlier filing.',
                        version: filing.version,
                      })}
                    </p>
                  )}
                  {filing.supersededBy && (
                    <p>
                      {t('filings.supersededNote', 'This version was superseded by a newer amendment and is kept for the audit trail.')}
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onDownload(filing)}>
                  <Download className="h-3.5 w-3.5" />
                  {t('filings.downloadFile', 'Download file')}
                </Button>
                {filing.status === 'generated' && canApprove && (
                  <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => onApprove(filing)}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {busy ? t('filings.approving', 'Approving…') : t('filings.approve', 'Approve')}
                  </Button>
                )}
              </div>
              {filing.status === 'generated' && canApprove && (
                <p className="-mt-4 text-xs text-muted-foreground">
                  {t('filings.secondReviewerHint', 'A second reviewer must approve — the person who generated the filing cannot approve it.')}
                </p>
              )}

              {/* Timeline */}
              <section>
                <h3 className="mb-3 text-sm font-semibold text-foreground">
                  {t('filings.timelineTitle', 'Timeline')}
                </h3>
                <ol className="space-y-3">
                  {timeline.map((s) => (
                    <li key={s.key} className="flex items-start gap-3">
                      <span
                        className={cn(
                          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                          s.done ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {s.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                      </span>
                      <div className="min-w-0">
                        <p className={cn('text-sm font-medium', s.done ? 'text-foreground' : 'text-muted-foreground')}>
                          {s.label}
                        </p>
                        {(s.actor || s.date) && (
                          <p className="text-xs text-muted-foreground">
                            {[s.actor, s.date ? formatDate(s.date) : ''].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {s.detail && <p className="text-xs text-muted-foreground">{s.detail}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              {/* Validation findings */}
              {filing.validation.length > 0 && (
                <section>
                  <h3 className="mb-3 text-sm font-semibold text-foreground">
                    {t('filings.validationSection', 'Validation findings')}
                  </h3>
                  <IssueList issues={filing.validation} />
                </section>
              )}

              {/* Notes */}
              {filing.notes && (
                <section>
                  <h3 className="mb-1 text-sm font-semibold text-foreground">{t('filings.notes', 'Notes')}</h3>
                  <p className="text-sm text-muted-foreground">{filing.notes}</p>
                </section>
              )}

              {/* Evidence */}
              <section>
                <h3 className="mb-3 text-sm font-semibold text-foreground">
                  {t('filings.evidenceTitle', 'Evidence & receipts')}
                </h3>
                {(filing.evidence?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('filings.evidenceEmpty', 'No attachments yet — keep the acknowledgment and payment receipt here.')}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {filing.evidence!.map((ev) => (
                      <li key={ev.id} className="flex items-center gap-3 rounded-lg border border-border p-2.5">
                        <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">{ev.fileName}</p>
                          <p className="text-xs text-muted-foreground">
                            {[formatBytes(ev.sizeBytes), ev.uploadedAt ? formatDate(ev.uploadedAt) : '', ev.note]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="gap-1.5"
                          onClick={() => onDownloadEvidence(filing, ev.id)}
                        >
                          <Download className="h-3.5 w-3.5" />
                          {t('filings.download', 'Download')}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}

                {canRun && (
                  <div className="mt-3 space-y-2 rounded-lg border border-dashed border-border p-3">
                    <input
                      ref={fileRef}
                      type="file"
                      className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:bg-muted/70"
                    />
                    <Input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={t('filings.evidenceNotePlaceholder', 'Note (optional) — e.g. "TRA payment receipt"')}
                    />
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-muted-foreground">
                        {t('filings.evidenceMaxHint', 'Max 15MB per file.')}
                      </p>
                      <Button size="sm" className="gap-1.5" disabled={uploading} onClick={() => void upload()}>
                        <UploadCloud className="h-3.5 w-3.5" />
                        {uploading ? t('filings.uploading', 'Uploading…') : t('filings.upload', 'Upload')}
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </>
        )}

        {!loading && !filing && notFound && (
          <div className="py-16 text-center">
            <FileCheck className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">{t('filings.notFound', 'Filing not found.')}</p>
            <Button variant="outline" className="mt-4" onClick={onClose}>
              {t('filings.backToRegister', 'Back to filings')}
            </Button>
          </div>
        )}

        {!loading && !filing && !notFound && (
          <div role="alert" className="py-16 text-center text-muted-foreground">
            <FileCheck className="mx-auto mb-3 h-8 w-8" />
            {t('filings.detailError', 'Could not load the filing.')}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
